/* RESPONSIBLE TEAM: team-frontend-tech */

/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This file contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this file can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

import ENV from 'embercom/config/environment';
import type Route from '@ember/routing/route';
import type RouteInfo from '@ember/routing/route-info';

class FakeController {}

type ReactRoute = Route & { reactEnabled(): boolean };
type ReactRouteConstuctor<T = any> = new (...args: any[]) => T;
type _ReactRouteOptions = {
  overrideRenderTemplate?: boolean; // ⚠️⚠️⚠️ Do not use this unless you know what you're doing. See #team-frontend-tech for more information.
};

/**
 * Generic constructor type that can create instances of type T.
 * This is the foundation for TypeScript mixin patterns - it represents
 * any class constructor that can be called with 'new'.
 */
type Constructor<T = {}> = new (...args: any[]) => T;

/**
 * Type-preserving mixin that combines the original route class with ReactRoute functionality.
 *
 * This complex type does three important things:
 * 1. Preserves all properties/methods from the original class T (via InstanceType<T>)
 * 2. Adds the new ReactRoute methods (reactEnabled, buildRouteInfoMetadata, etc.)
 * 3. Maintains the constructor signature of the original class (via & T)
 *
 * The result is a new constructor type that TypeScript understands has both
 * the original class's services/properties AND the ReactRoute functionality.
 * This allows child classes to inherit services without redeclaring them.
 */
type ReactRouteMixin<T extends Constructor<any>> = Constructor<
  InstanceType<T> & {
    reactEnabled(): boolean;
    buildRouteInfoMetadata(): { react: boolean };
    model(params: any, transition: any): any;
    setupController(controller: any, model: any, transition: any): void;
    renderTemplate(): void;
  }
> &
  T;

export const isReactRoute = (routeInfo?: RouteInfo) => {
  // hack to use parent root metadata if none on this route and parent is index
  if (!routeInfo?.metadata && routeInfo?.name.endsWith('.index')) {
    let metadata = routeInfo.parent?.metadata;
    return metadata && typeof metadata === 'object' && 'react' in metadata && metadata.react;
  }
  let metadata = routeInfo?.metadata;
  return metadata && typeof metadata === 'object' && 'react' in metadata && metadata.react;
};

/**
 * Type-preserving wrapper that adds React app rendering behavior to Ember routes.
 *
 * This function solves a critical TypeScript inheritance problem: when wrapping a class
 * with service declarations, the wrapper normally breaks type inference for child classes.
 * Our solution uses advanced TypeScript generics to preserve all original class properties,
 * including @service declarations, so child classes can inherit without redeclaring services.
 *
 * @param superclass The Ember route class to wrap (preserves all its types)
 * @param options ⚠️⚠️⚠️ Advanced use only ⚠️⚠️⚠️ — Do not use unless you know what you're doing. See #team-frontend-tech for more information.
 * @returns A new class constructor that maintains original types + ReactRoute functionality
 */
export function ReactRoute<T extends ReactRouteConstuctor>(
  superclass: T,
  options: _ReactRouteOptions = {},
): ReactRouteMixin<T> {
  options = {
    ...{
      overrideRenderTemplate: true,
    },
    ...options,
  };

  /**
   * Create and return the enhanced route class.
   *
   * We cast 'superclass' to Constructor<any> to satisfy TypeScript's mixin requirements,
   * then cast the entire result to ReactRouteMixin<T> to preserve the original class's
   * type information while adding ReactRoute functionality.
   */
  return class extends (superclass as Constructor<any>) {
    constructor(...args: any[]) {
      super(...args);
    }
    reactEnabled(): boolean {
      if (ENV.environment === 'test') {
        return false;
      }
      let app = super.modelFor('apps.app');
      let inboxWorkspace = super.modelFor('inbox.workspace');
      if (!app && !inboxWorkspace) {
        return false;
      }
      return super.reactEnabled();
    }

    buildRouteInfoMetadata() {
      return { react: this.reactEnabled() };
    }

    model(params: any, transition: any) {
      if (!this.reactEnabled()) {
        return super.model(params, transition);
      }
      return null;
    }

    setupController(controller: any, model: any, transition: any) {
      if (!this.reactEnabled()) {
        return super.setupController(controller, model, transition);
      }
    }

    renderTemplate() {
      let metadata = this.buildRouteInfoMetadata();

      if (metadata.react && options.overrideRenderTemplate) {
        let controller = new FakeController();

        super.render('react-route', { controller });
      } else {
        return super.renderTemplate(...arguments);
      }
    }
  } as ReactRouteMixin<T>;
}
